/**
 * Before / After Slider — animated wipe between a red "VORHER" and accent "NACHHER".
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

const VOR_COLOR = "#dc2626";

export const beforeAfterSlider: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const beforeText = inputs.text ?? "Vorher";
  const afterText = inputs.subtext ?? "Nachher";
  const beforeLabel = String(inputs.extras?.beforeLabel ?? "VORHER");
  const afterLabel = String(inputs.extras?.afterLabel ?? "NACHHER");
  const isVertical = ctx.format === "9:16";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const afterId = `${clipPrefix}-after`;
  const handleId = `${clipPrefix}-handle`;
  const beforeLabelId = `${clipPrefix}-blbl`;
  const afterLabelId = `${clipPrefix}-albl`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "solid", accent });

  const beforeLines = beforeText.split("\n");
  const afterLines = afterText.split("\n");

  const renderHalf = (lines: string[], textColor: string, align: "left" | "right", topLabel: string, labelBg: string) => {
    return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;${align === "left" ? "align-items:flex-start;text-align:left;" : "align-items:flex-end;text-align:right;"}justify-content:center;padding:${isVertical ? "120px 56px" : "80px 96px"};gap:14px;color:${textColor};">
      <div style="padding:8px 18px;border-radius:9999px;background:${labelBg};color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;box-shadow:0 0 28px ${withAlpha(labelBg, 0.55)};">${escapeHtml(topLabel)}</div>
      ${lines.map((line) => `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.65}px;letter-spacing:-2px;line-height:1.02;text-wrap:balance;max-width:${isVertical ? 720 : 760}px;">${escapeHtml(line)}</div>`).join("")}
    </div>`;
  };

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
    <div id="${stageId}" style="position:absolute;inset:0;background:linear-gradient(135deg, #1b0808 0%, #2a0e0e 100%);">
      ${renderHalf(beforeLines, withAlpha(themeColors.fg, 0.92), "left", beforeLabel, VOR_COLOR)}
    </div>
    <div id="${afterId}" style="position:absolute;inset:0;background:linear-gradient(135deg, ${withAlpha(accent, 0.35)} 0%, ${themeColors.bg} 70%);clip-path:inset(0% 100% 0% 0%);">
      ${renderHalf(afterLines, themeColors.fg, "right", afterLabel, accent)}
    </div>
    <div id="${handleId}" style="position:absolute;top:0;bottom:0;left:0%;width:4px;background:${accent};box-shadow:0 0 30px ${withAlpha(accent, 0.7)};z-index:5;">
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:56px;height:56px;border-radius:9999px;background:${accent};box-shadow:0 0 30px ${withAlpha(accent, 0.65)};display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:22px;">⇆</div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${afterId}`, { clipPath: "inset(0% 100% 0% 0%)" }),
    gsapSetLine(`#${handleId}`, { left: "0%" }),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${afterId}`, { clipPath: "inset(0% 0% 0% 0%)", duration: Math.max(1.4, clipDurationSec - 1.0), ease: "raw:\"power2.inOut\"" }, 0.5),
    t.to(`#${handleId}`, { left: "100%", duration: Math.max(1.4, clipDurationSec - 1.0), ease: "raw:\"power2.inOut\"" }, 0.5),
  ];
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));
  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
